// Header.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import AllApi from "../../../api/allApi";
import allApi from "../../../api/allApi";
import { useTheme } from "../../../app/component/ThemeContext";
import axios from "axios";
// import { useLanguage } from "../LanguageContext";
import { useLocalization } from "../localization/LocalizationContext.js";
import { useNetwork } from "../common/networkContext";
import { useSidebar } from "../common/sidebarContext.js";
import LogoutIcon from "@mui/icons-material/Logout";
import LanguageIcon from "@mui/icons-material/Language";
import MenuIcon from "@mui/icons-material/Menu";


export default function Header({ heading, showBreadcrumb, breadcrumbs }) {
  // const { changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const { selectedNetworkId } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const { localizationStrings, setLanguage, language } = useLocalization();
  const [nameInitials, setNameInitials] = useState("");
  const [userName, setUserName] = useState("");
  const [showBadge, setShowBadge] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const notificationRef = useRef(null);
  const toggleRef = useRef(null);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [mainNotifications, setMainNotifications] = useState([]);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const { isCollapsed, toggleMobileNav } = useSidebar();
  let isToggleClick = false;
  //const [bellIcon,setBellIcon] = useState(false);

  let isRefreshing = false;
  let refreshAttempts = 0;
  const MAX_REFRESH_ATTEMPTS = 2;

  useEffect(() => {
    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  useEffect(() => {
    const theme_value = localStorage.getItem("theme_key");
    if (theme_value == "dark") {
      document.documentElement.setAttribute("data-theme", "dark");
      document.getElementById("theme").checked = true;
    } else {
      document.documentElement.setAttribute("data-theme", "light");
    }
    const userName = localStorage.getItem("username");
    setUserName(userName);
    themeChange();

    if (userName) {
      const [firstName, lastName] = userName.split(" ");
      const initials = `${firstName.charAt(0).toUpperCase()}${lastName
        .charAt(0)
        .toUpperCase()}`;
      setNameInitials(initials);
    }
    fetchNotifications();
    fetchUnreadNotifications();
  }, [networkId]);

  useEffect(() => {
    const checkTokenAndRefresh = () => {
      const tokenExpiration = Number(localStorage.getItem("tokenExpiration"));
      const currentTime = Date.now();

      const FIVE_MINUTES = 5 * 60 * 1000;

      if (tokenExpiration && currentTime >= tokenExpiration - FIVE_MINUTES) {
        console.log("Token about to expire, refreshing...");
        refreshToken();
      }
    };

    const interval = setInterval(checkTokenAndRefresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const getSelectedLanguageLabel = () => {
    switch (language) {
      case "ko":
        return localizationStrings?.korean || "Korean";

      case "ja":
        return localizationStrings?.japanese || "Japanese";

      case "hi":
        return localizationStrings?.hindi || "Hindi";

      default:
        return localizationStrings?.english || "English";
    }
  };

  const handleLanguageChange = (languageCode) => {
    console.log("Selected language:", languageCode);
    setLanguage(languageCode);
  };

  const refreshToken = async () => {
    if (isRefreshing) return false; // prevent parallel calls

    if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      console.warn("Max refresh attempts reached. Logging out...");
      logout();
      return false;
    }

    try {
      isRefreshing = true;
      refreshAttempts++;

      const api = new allApi();
      const storedRefreshToken = localStorage.getItem("refreshToken");

      if (!storedRefreshToken) {
        logout();
        return false;
      }

      const payload = { refreshToken: storedRefreshToken };

      const response = await api.postRequest(
        "api/auth/refresh-token",
        "",
        payload
      );

      if (response?.token) {
        const { token, refreshToken: newRefreshToken } = response;

        const expirationTime = Date.now() + 24 * 3600 * 1000;

        localStorage.setItem("sessionToken", token);
        localStorage.setItem("refreshToken", newRefreshToken);
        localStorage.setItem("tokenExpiration", expirationTime);

        allApi.token = token;

        console.log("Token refreshed successfully");

        refreshAttempts = 0; // reset on success
        isRefreshing = false;
        return true;
      } else {
        logout();
        return false;
      }
    } catch (error) {
      console.error("Refresh failed:", error);

      isRefreshing = false;

      if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        logout();
      }

      return false;
    }
  };

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const refreshed = await refreshToken();

        if (refreshed) {
          return axios(originalRequest);
        } else {
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );

  const logout = () => {
    const allApi = new AllApi();
    allApi
      .logout("api/auth/logout")
      .then((response) => {
        localStorage.removeItem("sessionToken");
      })
      .catch((error) => {
        console.error("Logout Error:", error);
      });
    navigate("/");
  };

  const themeChange = () => {
    const toggleSwitch = document.getElementById("theme");
    function switchTheme(e) {
      if (e.target.checked) {
        document.documentElement.setAttribute("data-theme", "dark");
        localStorage.setItem("theme_key", "dark");
      } else {
        document.documentElement.setAttribute("data-theme", "light");
        localStorage.setItem("theme_key", "light");
      }
    }
    toggleSwitch.addEventListener("change", switchTheme, false);
  };

  const fetchNotifications = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest(`api/notification/allNotifications?networkId=${networkId}`);
      const allFetchedNotifications = response.map((notification) => ({
        id: notification.id || notification.notificationId,
        description: notification.description,
        createdAt: notification.createdAt,
        deviceName: notification.deviceName || "N/A",
        status: notification.status || 0,
      }));

      setAllNotifications(allFetchedNotifications);
      setNotifications(allFetchedNotifications.slice(0, 5));
      //  setNotificationCount(allFetchedNotifications.length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const markAsRead = async (notificationIds) => {
    try {
      const api = new allApi();
      const payload = notificationIds;
      await api.postRequest(`api/notification/markNotifications?networkId=${networkId}`, "", payload);

      setAllNotifications(
        allNotifications.filter((n) => !notificationIds.includes(n.id))
      );
      setNotifications(
        allNotifications.filter((n) => !notificationIds.includes(n.id))
      );
      setNotificationCount((prevCount) => prevCount - notificationIds.length);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/notification/allUnreadNotifications?networkId=${networkId}`
      );

      const unreadNotificationsList = response.map((notification) => ({
        id: notification.id || notification.notificationId,
        description: notification.description,
        createdAt: notification.createdAt,
        deviceName: notification.deviceName || "N/A",
      }));

      setUnreadNotifications(unreadNotificationsList);
      setNotificationCount(unreadNotificationsList.length);

      if (showOnlyUnread) {
        setNotifications(
          unreadNotificationsList.length > 0
            ? unreadNotificationsList.slice(0, 5)
            : []
        );
      }
    } catch (error) {
      console.error("Error fetching unread notifications", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotificationIds = allNotifications
        .filter((notification) => notification.status == 0)
        .map((notification) => notification.id);

      await markAsRead(unreadNotificationIds);

      const updatedNotifications = allNotifications.map((notification) => ({
        ...notification,
        status: 1,
      }));

      setAllNotifications(updatedNotifications);

      if (showOnlyUnread) {
        setNotifications([]);
      } else {
        setNotifications(updatedNotifications.slice(0, 5));
      }

      setNotificationCount(0);
      fetchUnreadNotifications();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const handleSingleRead = async (notificationId) => {
    if (showOnlyUnread) {
      markAsRead([notificationId]);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await fetchUnreadNotifications();
    }
  };

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
    //setBellIcon((prev)=> !prev);
  };

  const handleClickOutside = (event) => {
    if (isToggleClick) {
      isToggleClick = false; // Reset flag
      return;
    }

    if (
      notificationRef.current &&
      !notificationRef.current.contains(event.target) &&
      toggleRef.current &&
      !toggleRef.current.contains(event.target)
    ) {
      setShowNotifications(!showNotifications);
      setShowOnlyUnread(false);
      setNotifications(allNotifications.slice(0, 5));
      // setBellIcon(false);
    }
  };

  const handleUnreadClick = () => {
    setShowOnlyUnread((prev) => {
      const updatedState = !prev;

      // Update notifications based on the new state
      const updatedNotifications = updatedState
        ? allNotifications.filter((n) => n.status == 0).slice(0, 5) // Only unread
        : allNotifications.slice(0, 5); // Top 5 regardless of read status

      setNotifications(updatedNotifications);
      return updatedState; // Toggle showOnlyUnread
    });
  };

  const handleViewAllClick = () => {
    navigate("/notification");
    setShowNotifications(false);
  };

  const handleClick = () => {
    setShowBadge(true);
    navigate("/notification");
  };

  const disableMark = unreadNotifications.length === 0;

  return (
    <header id="top-bar" className={isCollapsed ? "sidebar-collapsed" : ""}>
      <div className="header-left">
        <button
          type="button"
          className="header-menu-btn"
          onClick={toggleMobileNav}
          aria-label="Open navigation"
        >
          <MenuIcon sx={{ fontSize: 22 }} />
        </button>
        <div className="header-title">{heading}</div>
      </div>
      <div className="header-control">
        <div className="switch-box">
          <label className="ice-switch">
            <input
              type="checkbox"
              className="theme"
              id="theme"
              checked={theme === "dark"}
              onChange={toggleTheme}
            />
            <span className="slider"></span>
          </label>
          <span className="header-text-label">{localizationStrings?.darkMode}</span>
        </div>

        <div className="header-divider" />

        <div className="user-access dropdown">
          <div data-bs-toggle="dropdown" aria-expanded="false">
            <div className="header-lang-trigger">
              <LanguageIcon sx={{ fontSize: 18 }} />
              <span className="lang-label">{getSelectedLanguageLabel()}</span>
            </div>
          </div>

          <div className="dropdown-menu dropdown-menu-end">
            <button
              className={`dropdown-item ${language === "en" ? "active" : ""}`}
              onClick={() => handleLanguageChange("en")}
            >
              {localizationStrings?.english}
            </button>
            <button
              className={`dropdown-item ${language === "ko" ? "active" : ""}`}
              onClick={() => handleLanguageChange("ko")}
            >
              {localizationStrings?.korean}
            </button>
            <button
              className={`dropdown-item ${language === "ja" ? "active" : ""}`}
              onClick={() => handleLanguageChange("ja")}
            >
              {localizationStrings?.japanese}
            </button>
            <button
              className={`dropdown-item ${language === "hi" ? "active" : ""}`}
              onClick={() => handleLanguageChange("hi")}
            >
              {localizationStrings?.hindi}
            </button>
          </div>
        </div>

        <div className="header-divider" />

        <div className="header-notification">
          <i
            className="icon notifications notifications-count"
            onClick={handleNotificationClick}
          >
            {notificationCount > 0 && <span>{notificationCount}</span>}
          </i>
          {showNotifications && (
            <div ref={notificationRef} className="top-notification">
              <div className="notification-title">
                {localizationStrings?.notifications}
                <div className="switch-box" ref={toggleRef}>
                  <label className="ice-switch">
                    <input
                      type="checkbox"
                      className="theme"
                      checked={showOnlyUnread}
                    // onChange={handleUnreadClick}
                    />
                    <span className="slider" onClick={handleUnreadClick}></span>
                  </label>
                  <span>{localizationStrings?.onlyShowUnread}</span>
                </div>
              </div>
              <div className="notification-box w-scroll">
                {showOnlyUnread ? (
                  unreadNotifications.length > 0 ? (
                    unreadNotifications.map((notification, index) => (
                      <div
                        className="notification-item"
                        key={index}
                        onClick={() => {
                          handleSingleRead(notification.id);
                          // handleViewAllClick();
                        }}
                      >
                        <div>
                          <strong>{notification.deviceName || "N/A"}</strong>{" "}
                          <span>
                            {new Date(notification.createdAt).toLocaleString()}
                          </span>{" "}
                        </div>
                        <div>
                          <span>{notification.description}</span>{" "}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-notifications-message">
                      {localizationStrings?.noUnreadNotifications}
                    </div>
                  )
                ) : notifications.length > 0 ? (
                  notifications.map((notification, index) => (
                    <div
                      className="notification-item"
                      key={index}
                      onClick={() => {
                        handleSingleRead(notification.id);
                        //  handleViewAllClick();
                      }}
                    >
                      <div>
                        <strong>{notification.deviceName || "N/A"}</strong>{" "}
                        <span>
                          {new Date(notification.createdAt).toLocaleString()}
                        </span>{" "}
                      </div>
                      <div>
                        <span>{notification.description}</span>{" "}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-notifications-message">
                    {localizationStrings?.noNotificationstoDisplay}
                  </div>
                )}
              </div>

              <div className="notification-footer">
                <span onClick={handleViewAllClick}>
                  {localizationStrings?.viewAll}
                </span>{" "}
                <span
                  className={`mark-all ${disableMark ? "disabled" : ""}`}
                  onClick={!disableMark ? handleMarkAllAsRead : null} // Prevent click when disabled
                  style={{
                    cursor: disableMark ? "not-allowed" : "pointer",
                    opacity: disableMark ? 0.5 : 1,
                  }}
                >
                  {localizationStrings?.markAllasRead}
                </span>{" "}
              </div>
            </div>
          )}
        </div>

        <div className="header-divider" />

        <div className="user-access dropdown">
          <div data-bs-toggle="dropdown" aria-expanded="false">
            <div className="header-user-trigger">
              <span className="profile-user dark">{nameInitials}</span>
              <span className="name-post">
                <strong>{userName}</strong>
              </span>
            </div>
          </div>
          <div className="dropdown-menu dropdown-menu-end">
            {/* <button className="dropdown-item">
      {localizationStrings?.profileSettings}
    </button> */}
            <button className="dropdown-item" onClick={logout}>
              <LogoutIcon sx={{ fontSize: 18, mr: 1 }} />
              {localizationStrings?.logout}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
